import classNames from 'classnames';
import { forwardRef } from 'react';

import { getUrlParam } from '~globals/common';

export type CardWithIconAndHiddenDescriptionProps = {
    title: string;
    body: string;
    toggle: string;
    hiddentext: string;
    imageurl: string;
    index: number;
    backgroundsurface: SupportedBackgroundColor;
    textcolor: SupportedTextColor;
    minwidths: string;
    maxheights?: string;
    urlflag?: 'DSNP';
    showingHiddenText: boolean;
    toggleDescription: boolean;
    setToggleDescription: (index: number) => void;
};

const CardWithIconAndHiddenDescription = forwardRef(function RefCard(
    {
        imageurl,
        title,
        body,
        toggle,
        hiddentext,
        index,
        backgroundsurface,
        textcolor,
        minwidths,
        maxheights,
        urlflag,
        showingHiddenText,
        toggleDescription,
        setToggleDescription,
    }: CardWithIconAndHiddenDescriptionProps,
    ref: React.ForwardedRef<HTMLDivElement | null>
) {
    const doesRenderWithCurrentFlag = () => {
        if (!urlflag) {
            return true;
        }
        if (urlflag.includes(':hide')) {
            const actualFlag = urlflag.replace(':hide', '');
            return getUrlParam(actualFlag) !== 'true';
        }
        return getUrlParam(urlflag) === 'true';
    };

    return doesRenderWithCurrentFlag() ? (
        <div
            className={classNames(
                'icon-hidden-desc-card flex flex-col lg:flex-row items-center justify-center self-stretch',
                'm-1 p-6 overflow-hidden relative',
                'snap-center md:snap-align-none rounded-md shadow',
                minwidths,
                maxheights,
                backgroundsurface,
                textcolor as string,
                'data-[show="true"]:data-[active="true"]:min-w-full',
                // tablet card width adjustments
                'data-[show="true"]:data-[active="false"]:md:w-[246px]',
                'data-[show="true"]:data-[active="true"]:md:w-[288px]',
                // desktop card width adjustments
                'data-[show="true"]:data-[active="false"]:lg:w-[230px]',
                'data-[show="true"]:data-[active="true"]:lg:w-[486px]'
            )}
            data-show={showingHiddenText}
            data-active={toggleDescription}
            ref={ref}
            data-index={index}
        >
            <div
                className={classNames(
                    'grid grid-cols-1 lg:!grid-rows-4-min-content lg:!grid-cols-1 items-center justify-items-center lg:!justify-items-center self-stretch w-auto',
                    'data-[active="true"]:grid-cols-2-auto data-[active="true"]:grid-rows-2 data-[active="true"]:justify-items-start',
                    'data-[active="false"]:w-full'
                )}
                data-active={toggleDescription}
            >
                <img
                    alt=""
                    src={imageurl}
                    width={80}
                    height={80}
                    className={classNames(
                        'lg:!row-span-1 mb-4 lg:!mb-4 min-w-[136px] w-[136px] h-[136px]',
                        'data-[active="true"]:m-0 data-[active="true"]:min-w-[60px] data-[active="true"]:w-[60px] data-[active="true"]:h-[60px]',
                        'data-[active="true"]:row-span-2',
                        'md:min-w-[156px] md:w-[156px] md:h-[156px]',
                        'lg:!min-w-[183px] lg:!w-[183px] lg:!h-[183px]'
                    )}
                    data-active={toggleDescription}
                />
                <h4
                    className={classNames(
                        'lg:!col-start-1 text-20 leading-28 font-bold font-poppins text-center mb-1 lg:!mb-1 w-full',
                        'data-[active="true"]:col-start-2 data-[active="true"]:m-0 data-[active="true"]:text-left data-[active="true"]:lg:text-center'
                    )}
                    data-active={toggleDescription}
                    dangerouslySetInnerHTML={{ __html: title }}
                ></h4>
                <p
                    className={classNames(
                        'lg:!col-start-1 text-14 leading-20 text-center w-full',
                        'data-[active="true"]:col-start-2 data-[active="true"]:text-left data-[active="true"]:lg:text-center'
                    )}
                    data-active={toggleDescription}
                    dangerouslySetInnerHTML={{ __html: body }}
                ></p>
                <button
                    type="button"
                    className='bg-transparent mx-0 my-2 outline-none px-0 py-2 text-16 leading-24 text-center text-primary data-[show="true"]:data-[active="true"]:hidden'
                    data-show={showingHiddenText}
                    data-active={toggleDescription}
                    aria-hidden={!toggleDescription}
                    onClick={() => setToggleDescription(index)}
                    dangerouslySetInnerHTML={{ __html: toggle }}
                    data-qa={`view-my-bio-agent-${index}`}
                />
            </div>
            <p
                className={classNames(
                    'text-16 leading-24 overflow-hidden',
                    'invisible opacity-0 h-0 w-full lg:h-auto lg:!mt-0 lg:w-0',
                    'data-[active="true"]:opacity-100 data-[active="true"]:visible data-[active="true"]:h-full',
                    'data-[active="true"]:mt-1 data-[active="true"]:lg:ml-4 data-[active="true"]:lg:w-60'
                )}
                data-active={toggleDescription}
                dangerouslySetInnerHTML={{ __html: hiddentext }}
            ></p>
        </div>
    ) : null;
});

CardWithIconAndHiddenDescription.displayName = 'CardWithIconAndHiddenDescription';

export default CardWithIconAndHiddenDescription;
